import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { ButtonComponent } from './../../../shared/components/custom-button/button.component';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs';
import { AddCategoryComponent } from '../../../shared/components/dialog/add-category/add-category.component';
import { AlertDialogComponent } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { InputComponent } from '../../../shared/components/from-input/input.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

interface ParentCategoryResponse {
  categoryName: string;
  faqCategoryId: string;
  lastUpdatedDisplay: string;
  isActive: boolean | string;
  faQs: SubCategoryResponse[];
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: any;
}

interface SubCategoryResponse {
  faqId?: string;
  question: string;
  answer: string;
  faqCategoryId: string;
  lastUpdatedDisplay: string;
  isActive?: boolean;
  displayOrder?: number;
}

interface SubCategory {
  id: string;
  question: string;
  answer: string;
  lastUpdatedDisplay: string;
  categoryId: string;
  displayOrder?: number;
}

interface ParentCategory {
  id: string;
  name: string;
  subCategories: SubCategory[];
}

interface ReorderPayload {
  faqId: string;
  displayOrder: number;
}

@Component({
  selector: 'app-faq',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    InputComponent,
    ButtonComponent,
  ],
  templateUrl: './event-category-type.component.html',
  styleUrls: ['./event-category-type.component.scss'],
})
export class EventCategoriesTypeComponent implements OnInit {
  selectedParentCategory: string = '';
  showAddForm: boolean = false;
  editingSubCategoryId: string | null = null;
  isAdminMode: boolean = false;
  parentCategoryControl = new FormControl('');
  isLoading: boolean = false;
  parentCategoryOptions: any = [];

  categoryErrorMessages = {
    required: 'Please select a parent category.',
  };

  parentCategories: ParentCategory[] = [];
  allSubCategories: SubCategory[] = [];
  currentSubCategories: SubCategory[] = [];

  newSubCategory: SubCategory = {
    id: '',
    question: '',
    answer: '',
    lastUpdatedDisplay: '',
    categoryId: '',
  };

  editingSubCategory: SubCategory = {
    id: '',
    question: '',
    answer: '',
    lastUpdatedDisplay: '',
    categoryId: '',
  };

  constructor(
    private location: Location,
    private dialog: MatDialog,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
  ) {
    const role = localStorage.getItem('userRole');
    this.isAdminMode =
      role === 'csqadmin' || role === 'superadmin' ? true : false;
  }

  ngOnInit(): void {
    this.loadParentCategoryDropdown().subscribe();
    this.loadSubCategoryList().subscribe();

    this.parentCategoryControl.valueChanges.subscribe((value) => {
      if (value) {
        this.selectedParentCategory = value;
        this.newSubCategory.categoryId = value;
        this.refreshCurrentSubCategories();
        this.closeAddForm();
        this.clearEdit();
      } else {
        this.selectedParentCategory = '';
        this.currentSubCategories = [];
        this.closeAddForm();
        this.clearEdit();
      }
    });
  }

  get filteredSubCategories(): SubCategory[] {
    if (!this.selectedParentCategory) {
      return [];
    }

    const filtered = this.allSubCategories.filter(
      (item) => item.categoryId === this.selectedParentCategory,
    );

    return filtered.sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
    );
  }

  get hasSubCategoriesInSelected(): boolean {
    return this.filteredSubCategories.length > 0;
  }

  private refreshCurrentSubCategories(): void {
    if (!this.selectedParentCategory) {
      this.currentSubCategories = [];
      return;
    }

    const items = this.allSubCategories.filter(
      (item) => item.categoryId === this.selectedParentCategory,
    );

    this.currentSubCategories = items.sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
    );
  }

  goBack(): void {
    this.location.back();
  }

  loadParentCategoryDropdown() {
    return this.httpService.get<ApiDataResponse>(`faqcategories`).pipe(
      tap({
        next: (response) => {
          if (response.status && Array.isArray(response.data)) {
            const categoryData = response.data as ParentCategoryResponse[];

            this.parentCategoryOptions = categoryData.map((item) => ({
              label: item.categoryName,
              value: item.faqCategoryId,
            }));

            this.parentCategories = categoryData.map((item) => ({
              id: item.faqCategoryId,
              name: item.categoryName,
              subCategories: [],
            }));
          } else {
            this.parentCategoryOptions = [];
            this.parentCategories = [];
          }
        },
        error: (err) => {
          this.showErrorToast(err?.message);
          this.parentCategoryOptions = [];
          this.parentCategories = [];
        },
      }),
    );
  }

  loadSubCategoryList() {
    return this.httpService.get<ApiDataResponse>(`faqs/all`).pipe(
      tap({
        next: (response) => {
          if (response.status && Array.isArray(response.data)) {
            const categoryData = response.data as ParentCategoryResponse[];

            const processedCategories = categoryData.map((category) => ({
              id: category.faqCategoryId,
              name: category.categoryName,
              subCategories: category.faQs
                ? category.faQs.map((item, index) => ({
                    id: item.faqId || this.generateTempId(),
                    question: item.question,
                    answer: item.answer,
                    categoryId: category.faqCategoryId,
                    lastUpdatedDisplay:
                      item.lastUpdatedDisplay || category.lastUpdatedDisplay,
                    displayOrder: item.displayOrder || index + 1,
                  }))
                : [],
            }));

            this.parentCategories = this.parentCategories.map((category) => {
              const updated = processedCategories.find(
                (cat) => cat.id === category.id,
              );
              return updated
                ? { ...category, subCategories: updated.subCategories }
                : category;
            });

            this.allSubCategories = processedCategories
              .flatMap((category) => category.subCategories)
              .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

            if (this.selectedParentCategory) {
              this.refreshCurrentSubCategories();
            }
          } else {
            this.allSubCategories = [];
            this.currentSubCategories = [];
          }
        },
        error: (err) => {
          this.showErrorToast(err?.message);
          this.allSubCategories = [];
          this.currentSubCategories = [];
        },
      }),
    );
  }

  saveSubCategory(subCategoryData: SubCategory, isEdit: boolean = false) {
    if (!this.selectedParentCategory) {
      this.showErrorToast('Please select a parent category first.');
      this.isLoading = false;
      return;
    }

    const payload = {
      question: subCategoryData.question.trim(),
      answer: subCategoryData.answer.trim(),
      faqCategoryId: this.selectedParentCategory,
      isActive: true,
    };

    let apiCall;

    if (isEdit && subCategoryData.id && !subCategoryData.id.startsWith('temp_')) {
      apiCall = this.httpService.put<ApiDataResponse>(
        `faqs/${subCategoryData.id}`,
        payload,
        true,
      );
    } else {
      apiCall = this.httpService.post<ApiDataResponse>('faqs', payload, true);
    }

    apiCall.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: (response) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.isLoading = false;
          this.loadSubCategoryList().subscribe();

          if (isEdit) {
            this.clearEdit();
          } else {
            this.closeAddForm();
          }
        } else {
          this.showErrorToast(response.message);
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.showErrorToast(err?.message);
        this.isLoading = false;
      },
    });
  }

  getParentCategoryTitle(): string {
    const category = this.parentCategories.find(
      (cat) => cat.id === this.selectedParentCategory,
    );
    return category
      ? `Event category type in ${category.name}`
      : 'Select a category to view sub categories';
  }

  openAddSubCategory(): void {
    if (!this.selectedParentCategory) {
      this.showErrorToast('Please select a parent category first.');
      return;
    }

    const dialogRef = this.dialog.open(AddCategoryComponent, {
      width: '34.375rem',
      maxWidth: '34.375rem',
      minWidth: '34.375rem',
      data: { initialData: undefined, EditMode: false, type: true },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadSubCategoryList().subscribe();
        this.showSuccessToast('Sub category added successfully!');
      }
    });
  }

  openEditSubCategory(subCategory: SubCategory): void {
    const dialogRef = this.dialog.open(AddCategoryComponent, {
      width: '34.375rem',
      maxWidth: '34.375rem',
      minWidth: '34.375rem',
      data: { initialData: subCategory, EditMode: true, type: true },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadSubCategoryList().subscribe();
        this.showSuccessToast('Sub category updated successfully!');
      }
    });
  }

  closeAddForm(): void {
    this.showAddForm = false;
    this.newSubCategory = {
      id: '',
      question: '',
      answer: '',
      lastUpdatedDisplay: '',
      categoryId: this.selectedParentCategory,
    };
  }

  editSubCategory(subCategory: SubCategory): void {
    this.openEditSubCategory(subCategory);
  }

  clearEdit(): void {
    this.editingSubCategoryId = null;
    this.editingSubCategory = {
      id: '',
      question: '',
      answer: '',
      lastUpdatedDisplay: '',
      categoryId: '',
    };
  }

  deleteSubCategory(subCategory: SubCategory): void {
    const dialogData = {
      title: 'Confirm delete',
      message: `Are you sure you want to delete this sub category?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result && subCategory.id && !subCategory.id.startsWith('temp_')) {
          this.httpService
            .delete<ApiDataResponse>(`faqs/${subCategory.id}`)
            .subscribe({
              next: (response) => {
                if (response.status) {
                  this.showSuccessToast(response.message);
                  this.loadSubCategoryList().subscribe();
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => {
                this.showErrorToast(err?.message);
              },
            });
        } else if (result && subCategory.id.startsWith('temp_')) {
          this.allSubCategories = this.allSubCategories.filter(
            (item) => item.id !== subCategory.id,
          );
          this.refreshCurrentSubCategories();
          this.showSuccessToast('Sub category removed successfully.');
        }
      });
  }

  moveUp(index: number): void {
    if (index > 0) {
      const current = this.currentSubCategories[index];
      const previous = this.currentSubCategories[index - 1];
      const tempOrder = current.displayOrder;
      current.displayOrder = previous.displayOrder;
      previous.displayOrder = tempOrder;
      [this.currentSubCategories[index], this.currentSubCategories[index - 1]] =
        [this.currentSubCategories[index - 1], this.currentSubCategories[index]];
      this.syncAllSubCategoriesOrder();
      this.updateOrderInBackend([current, previous]);
    }
  }

  moveDown(index: number): void {
    if (index < this.currentSubCategories.length - 1) {
      const current = this.currentSubCategories[index];
      const next = this.currentSubCategories[index + 1];
      const tempOrder = current.displayOrder;
      current.displayOrder = next.displayOrder;
      next.displayOrder = tempOrder;
      [this.currentSubCategories[index], this.currentSubCategories[index + 1]] =
        [this.currentSubCategories[index + 1], this.currentSubCategories[index]];
      this.syncAllSubCategoriesOrder();
      this.updateOrderInBackend([current, next]);
    }
  }

  onDrop(event: CdkDragDrop<SubCategory[]>): void {
    moveItemInArray(this.allSubCategories, event.previousIndex, event.currentIndex);
    moveItemInArray(this.currentSubCategories, event.previousIndex, event.currentIndex);
    this.currentSubCategories.forEach((item, index) => {
      item.displayOrder = index + 1;
    });
    this.syncAllSubCategoriesOrder();
    this.saveReorderedSubCategories();
  }

  private syncAllSubCategoriesOrder(): void {
    this.currentSubCategories.forEach((item) => {
      const index = this.allSubCategories.findIndex((s) => s.id === item.id);
      if (index !== -1) {
        this.allSubCategories[index].displayOrder = item.displayOrder;
      }
    });

    this.allSubCategories.sort((a, b) => {
      if (a.categoryId !== b.categoryId) {
        return a.categoryId.localeCompare(b.categoryId);
      }
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });
  }

  private updateOrderInBackend(itemsToUpdate: SubCategory[]): void {
    const reorderPayload: ReorderPayload[] = itemsToUpdate
      .filter((item) => item.id && !item.id.startsWith('temp_'))
      .map((item) => ({
        faqId: item.id,
        displayOrder: item.displayOrder || 0,
      }));

    if (reorderPayload.length > 0) {
      this.httpService
        .put<ApiDataResponse>('faqs/reorder', reorderPayload, true)
        .subscribe({
          next: (response) => {
            if (!response.status) {
              this.loadSubCategoryList().subscribe();
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
            this.loadSubCategoryList().subscribe();
          },
        });
    }
  }

  private saveReorderedSubCategories(): void {
    const reorderPayload: ReorderPayload[] = this.currentSubCategories
      .filter((item) => item.id && !item.id.startsWith('temp_'))
      .map((item, index) => ({
        faqId: item.id,
        displayOrder: index + 1,
      }));

    if (reorderPayload.length > 0) {
      this.httpService
        .put<ApiDataResponse>('faqs/reorder', reorderPayload, true)
        .subscribe({
          next: (response) => {
            if (!response.status) {
              this.loadSubCategoryList().subscribe();
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
            this.loadSubCategoryList().subscribe();
          },
        });
    }
  }

  openAddParentCategory(): void {
    const dialogRef = this.dialog.open(AddCategoryComponent, {
      width: '34.375rem',
      maxWidth: '34.375rem',
      minWidth: '34.375rem',
      data: { initialData: undefined, EditMode: false, type: false },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadParentCategoryDropdown().subscribe();
        this.loadSubCategoryList().subscribe();
      }
    });
  }

  private generateTempId(): string {
    return 'temp_' + Math.random().toString(36).substr(2, 9);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}